import { type FormEvent, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { BellRing, Mail, ShieldAlert } from "lucide-react";
import { devError, devLog } from "../../utils/devLog";
import {
  CUSTOM_CASE_COMING_SOON_FORMSPREE_ENDPOINT,
  CUSTOM_CASE_PRODUCT_PATH,
  formatSuspectDetails,
  getLastCustomOrderAccessToken,
  getLastCustomOrderEmail,
  loadStoredCustomCaseFormData,
} from "../../utils/customCase";

const normalizeString = (value = "") => String(value || "").trim();

type CustomCaseNotifyMePanelProps = {
  sourceContext: string;
  technicalIssueNote: string;
  accessToken?: string;
  shareToken?: string;
  evidenceId?: string;
  orderStatus?: string;
  generatedCaseId?: string;
  generationRunId?: string;
  elapsedLabel?: string;
  caseTitle?: string;
  city?: string;
  suspectNames?: string[];
  compact?: boolean;
  headline?: string;
  description?: string;
  successMessage?: string;
  notePlaceholder?: string;
  firstNameLabel?: string;
  firstNamePlaceholder?: string;
  emailLabel?: string;
  emailPlaceholder?: string;
  messageLabel?: string;
  submitIdleLabel?: string;
  submitBusyLabel?: string;
  genericErrorMessage?: string;
  missingEndpointMessage?: string;
  missingConfigErrorMessage?: string;
};

const CustomCaseNotifyMePanel = ({
  sourceContext,
  technicalIssueNote,
  accessToken = "",
  shareToken = "",
  evidenceId = "",
  orderStatus = "",
  generatedCaseId = "",
  generationRunId = "",
  elapsedLabel = "",
  caseTitle = "",
  city = "",
  suspectNames = [],
  compact = false,
  headline = "Want us to follow up instead of you waiting here?",
  description = "Leave the same email you used for checkout and we will contact you when your case is ready.",
  successMessage = "We have your details. We will let you know when your case is ready.",
  notePlaceholder = "Anything helpful for us to know about this custom case?",
  firstNameLabel = "First name",
  firstNamePlaceholder = "Your first name",
  emailLabel = "Email",
  emailPlaceholder = "you@example.com",
  messageLabel = "Message",
  submitIdleLabel = "Notify Me",
  submitBusyLabel = "Sending...",
  genericErrorMessage = "Unable to send the request right now.",
  missingEndpointMessage = "Missing `VITE_CUSTOM_CASE_COMING_SOON_FORMSPREE_ENDPOINT`.",
  missingConfigErrorMessage = "Formspree is not configured yet.",
}: CustomCaseNotifyMePanelProps) => {
  const location = useLocation();
  const [{ firstName, email, note }, setFormState] = useState({
    firstName: "",
    email: "",
    note: "",
  });
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const context = useMemo(() => {
    const formData = loadStoredCustomCaseFormData();
    const storedSuspects = Array.isArray(formData?.suspects)
      ? formData.suspects.map((suspect, index) => formatSuspectDetails(suspect, index)).join(" | ")
      : "";
    const now = new Date();

    return {
      formData,
      storedSuspects,
      lastOrderAccessToken: getLastCustomOrderAccessToken(),
      lastOrderEmail: getLastCustomOrderEmail(),
      routePath: location.pathname,
      routeSearch: location.search,
      routeHash: location.hash,
      clientIsoTimestamp: now.toISOString(),
      clientLocalTimestamp: now.toLocaleString(),
      visibleSuspects: Array.isArray(suspectNames) ? suspectNames.filter(Boolean).join(", ") : "",
    };
  }, [location.hash, location.pathname, location.search, suspectNames]);

  useEffect(() => {
    const fallbackFirstName = normalizeString(context.formData?.firstName);
    const fallbackEmail =
      normalizeString(context.formData?.email).toLowerCase() ||
      normalizeString(context.lastOrderEmail).toLowerCase();

    setFormState((current) => ({
      ...current,
      firstName: current.firstName || fallbackFirstName,
      email: current.email || fallbackEmail,
    }));

    devLog("route", "custom_case_notify_panel_viewed", {
      route: location.pathname,
      sourceContext,
      orderStatus,
      generatedCaseId,
      generationRunId,
      elapsedLabel,
      hasFormspreeEndpoint: Boolean(CUSTOM_CASE_COMING_SOON_FORMSPREE_ENDPOINT),
    });
  }, [
    context.formData?.email,
    context.formData?.firstName,
    context.lastOrderEmail,
    elapsedLabel,
    generatedCaseId,
    generationRunId,
    location.pathname,
    orderStatus,
    sourceContext,
  ]);

  const isConfigured = Boolean(CUSTOM_CASE_COMING_SOON_FORMSPREE_ENDPOINT);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isConfigured) {
      setStatus("error");
      setErrorMessage(missingConfigErrorMessage);
      devError("api", "custom_case_notify_submit_blocked", {
        route: location.pathname,
        sourceContext,
        reason: "missing_formspree_endpoint",
      });
      return;
    }

    setStatus("submitting");
    setErrorMessage("");

    const payload = new FormData();
    payload.set("firstName", normalizeString(firstName));
    payload.set("email", normalizeString(email).toLowerCase());
    payload.set("note", normalizeString(note));
    payload.set("_subject", `Custom case notify request from ${normalizeString(email).toLowerCase() || "unknown"}`);
    payload.set("sourceContext", sourceContext);
    payload.set("routePath", context.routePath);
    payload.set("routeSearch", context.routeSearch);
    payload.set("routeHash", context.routeHash);
    payload.set("accessToken", normalizeString(accessToken));
    payload.set("shareToken", normalizeString(shareToken));
    payload.set("evidenceId", normalizeString(evidenceId));
    payload.set("clientIsoTimestamp", context.clientIsoTimestamp);
    payload.set("clientLocalTimestamp", context.clientLocalTimestamp);
    payload.set("customCaseProductPath", CUSTOM_CASE_PRODUCT_PATH);
    payload.set("lastOrderAccessToken", context.lastOrderAccessToken);
    payload.set("lastOrderEmail", context.lastOrderEmail);
    payload.set("storedCity", normalizeString(context.formData?.city));
    payload.set("storedLocation", normalizeString(context.formData?.location));
    payload.set("storedSource", normalizeString(context.formData?.source));
    payload.set("storedSuspectCount", String(Array.isArray(context.formData?.suspects) ? context.formData.suspects.length : 0));
    payload.set("storedSuspects", context.storedSuspects);
    payload.set("selectedCaseTitle", normalizeString(caseTitle));
    payload.set("selectedCity", normalizeString(city));
    payload.set("visibleSuspects", context.visibleSuspects);
    payload.set("orderStatus", normalizeString(orderStatus));
    payload.set("generatedCaseId", normalizeString(generatedCaseId));
    payload.set("generationRunId", normalizeString(generationRunId));
    payload.set("elapsedLabel", normalizeString(elapsedLabel));
    payload.set("technicalIssueNote", normalizeString(technicalIssueNote));

    devLog("api", "custom_case_notify_submit_started", {
      route: location.pathname,
      sourceContext,
      orderStatus,
      generatedCaseId,
      generationRunId,
      elapsedLabel,
      hasAccessToken: Boolean(accessToken),
    });

    try {
      const response = await fetch(CUSTOM_CASE_COMING_SOON_FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
        body: payload,
      });

      if (!response.ok) {
        const responseText = await response.text();
        throw new Error(responseText || "formspree_request_failed");
      }

      setStatus("success");
      devLog("api", "custom_case_notify_submit_succeeded", {
        route: location.pathname,
        sourceContext,
        email: normalizeString(email).toLowerCase(),
        generatedCaseId,
        generationRunId,
      });
    } catch (error: any) {
      const message = String(error?.message || genericErrorMessage);
      setStatus("error");
      setErrorMessage(message);
      devError("api", "custom_case_notify_submit_failed", {
        route: location.pathname,
        sourceContext,
        message,
        generatedCaseId,
        generationRunId,
      });
    }
  };

  return (
    <div
      className={`rounded-[24px] border border-amber-300/18 bg-[linear-gradient(180deg,rgba(20,14,12,0.78),rgba(255,255,255,0.02))] ${
        compact ? "p-4" : "p-5"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-amber-300/20 bg-amber-400/10 text-amber-100">
          <BellRing className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-white">{headline}</p>
          <p className="mt-1 text-sm leading-6 text-white/72">{description}</p>
        </div>
      </div>

      <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-white/52">{firstNameLabel}</span>
            <input
              className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none transition focus:border-amber-300/45 focus:bg-white/7"
              value={firstName}
              onChange={(event) => setFormState((current) => ({ ...current, firstName: event.target.value }))}
              placeholder={firstNamePlaceholder}
              autoComplete="given-name"
            />
          </label>
          <label className="block">
            <span className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-white/52">{emailLabel}</span>
            <input
              type="email"
              required
              className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none transition focus:border-amber-300/45 focus:bg-white/7"
              value={email}
              onChange={(event) => setFormState((current) => ({ ...current, email: event.target.value }))}
              placeholder={emailPlaceholder}
              autoComplete="email"
            />
          </label>
        </div>

        <label className="block">
          <span className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-white/52">{messageLabel}</span>
          <textarea
            className="min-h-[112px] w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white outline-none transition focus:border-amber-300/45 focus:bg-white/7"
            value={note}
            onChange={(event) => setFormState((current) => ({ ...current, note: event.target.value }))}
            placeholder={notePlaceholder}
          />
        </label>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={!isConfigured || status === "submitting"}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-white/70"
          >
            <Mail className="h-4 w-4" />
            {status === "submitting" ? submitBusyLabel : submitIdleLabel}
          </button>
          {elapsedLabel ? (
            <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-white/58">
              {elapsedLabel}
            </span>
          ) : null}
        </div>
      </form>

      {status === "success" ? (
        <div className="mt-4 rounded-2xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3 text-sm text-emerald-100">
          {successMessage}
        </div>
      ) : null}

      {status === "error" ? (
        <div className="mt-4 rounded-2xl border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
          {errorMessage || genericErrorMessage}
        </div>
      ) : null}

      {!isConfigured ? (
        <div className="mt-4 rounded-2xl border border-amber-400/20 bg-amber-400/10 px-4 py-3 text-sm text-amber-100">
          <div className="flex items-start gap-2">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{missingEndpointMessage}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CustomCaseNotifyMePanel;
